import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSections } from '../../../actions/section.action';
import { getSurveysSummary } from '../../../actions/survey.summary.action';
import {
  clearSurveyDetailsCacheApi,
  createSurveyMetadataDescriptionApi,
  getSurveyDetailFiltersApi,
  getSurveyLocationFilterApi,
  getSurveyMetadataDescriptionApi,
  getSurveyVisualizationsApi,
  updateSurveyMetadataApi,
  updateSurveyMetadataDescriptionApi,
} from '../../../api/surveys';
import { getNumericString } from '../../../helpers/helpers';
import { getSurveyDescription } from '../utils/surveyDescription';

// Loads everything the wizard needs for a survey. The surveys summary and the
// section metadata list stay in redux because shared components
// (MoveQuestionToSection, modals) read them from the store; everything else is
// plain hook state.
const useSurveyData = (surveyId) => {
  const dispatch = useDispatch();
  const jwt = useSelector((state) => state.session.jwt);
  const { surveys } = useSelector((state) => state.surveySummary);
  const { allSections } = useSelector((state) => state.sections);

  const [metadataDescriptions, setMetadataDescriptions] = useState([]);
  const [visualizations, setVisualizations] = useState();
  const [surveyLocationFilter, setSurveyLocationFilter] = useState();
  const [surveyFilters, setSurveyFilters] = useState();
  // Local edits applied on top of the summary entry after "Survey information"
  // saves, so the UI reflects them without refetching the whole summary.
  const [surveyOverrides, setSurveyOverrides] = useState({});

  useEffect(() => {
    if (!jwt) return;
    dispatch(getSurveysSummary(jwt));
    dispatch(getSections(jwt));
    getSurveyMetadataDescriptionApi(jwt)
      .then((data) => setMetadataDescriptions(data || []))
      .catch((e) => console.error(e));
  }, [dispatch, jwt]);

  const refreshPreviewData = useCallback(async () => {
    if (!surveyId || !jwt) return;
    try {
      const [visualizationsData, locationFilterData, filtersData] = await Promise.all([
        getSurveyVisualizationsApi(surveyId, jwt),
        getSurveyLocationFilterApi(surveyId, jwt),
        getSurveyDetailFiltersApi(surveyId, jwt),
      ]);
      setVisualizations(visualizationsData);
      setSurveyLocationFilter(locationFilterData);
      setSurveyFilters(filtersData);
    } catch (e) {
      console.error(e);
    }
  }, [surveyId, jwt]);

  useEffect(() => {
    refreshPreviewData();
  }, [refreshPreviewData]);

  const refetchSections = useCallback(() => dispatch(getSections(jwt)), [dispatch, jwt]);

  const currentSurvey = useMemo(() => {
    const survey = surveys?.find((s) => Number(s.id) === Number(surveyId));
    if (!survey) return undefined;
    return {
      ...survey,
      ...surveyOverrides,
      surveySummaryDetailsDto: {
        ...survey.surveySummaryDetailsDto,
        ...surveyOverrides.surveySummaryDetailsDto,
      },
    };
  }, [surveys, surveyId, surveyOverrides]);

  const saveSurveyInfo = useCallback(
    async ({ title, description, dateAdministrated }) => {
      clearSurveyDetailsCacheApi(jwt).catch((e) => console.error(e));
      await updateSurveyMetadataApi(currentSurvey.id, { title }, jwt);

      const metaDescription = getSurveyDescription(currentSurvey, metadataDescriptions);
      if (metaDescription) {
        const updatedData = {
          ...metaDescription.Data,
          link: metaDescription?.Data?.link ?? 'https://www.rand.org/',
          administered: dateAdministrated,
          description,
        };
        await updateSurveyMetadataDescriptionApi(metaDescription.Id, updatedData, jwt);
        setMetadataDescriptions((prev) =>
          prev.map((desc) =>
            desc.Id === metaDescription.Id ? { ...desc, Data: updatedData } : desc
          )
        );
      } else {
        const newMetadataDescription = {
          subtype: currentSurvey.subType,
          type: currentSurvey.type,
          year: `${currentSurvey.year}_${getNumericString(currentSurvey.month)}`,
          link: 'https://www.rand.org/',
          administered: dateAdministrated,
          description,
        };
        await createSurveyMetadataDescriptionApi(newMetadataDescription, jwt);
        setMetadataDescriptions((prev) => [...prev, { Id: -1, Data: newMetadataDescription }]);
      }

      setSurveyOverrides((prev) => ({
        ...prev,
        title,
        surveySummaryDetailsDto: {
          ...prev.surveySummaryDetailsDto,
          description,
          dateAdministrated,
        },
      }));
    },
    [jwt, currentSurvey, metadataDescriptions]
  );

  return {
    jwt,
    currentSurvey,
    allSections,
    metadataDescriptions,
    visualizations,
    surveyLocationFilter,
    surveyFilters,
    loading: !currentSurvey,
    refreshPreviewData,
    refetchSections,
    saveSurveyInfo,
  };
};

export default useSurveyData;
